import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FREE_TIER_NOTE } from "utils/freeTierUtils";
import { FreeTierPrice, PricingRateRows } from "components/FreeTier";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { classNames } from "components/utils";

interface PriceSectionProps {
  displayPrice: string;
  disclaimer?: string;
  hasPrice: boolean;
  /** When set, replaces the computed price display with this label (e.g. "1 hour free"). */
  priceLabel?: string;
  /** True once both the instance and (for elastic storage) the storage value
   *  have been resolved. PriceSection won't animate until after first settlement
   *  so that the auto-population sequence on initial load is silent. */
  isPriceSettled?: boolean;
  isFreeTier?: boolean;
  isEntitled?: boolean;
  computePrice?: number;
  storageCost?: number;
  pricePerGbHr?: number;
  isStorageFree?: boolean;
  stoppable?: boolean;
  inline?: boolean;
  compact?: boolean;
}

export const PriceSection: React.FC<PriceSectionProps> = ({
  displayPrice,
  disclaimer,
  hasPrice,
  priceLabel,
  isPriceSettled = false,
  isFreeTier = false,
  isEntitled = false,
  computePrice = 0,
  storageCost = 0,
  pricePerGbHr = 0,
  isStorageFree = false,
  stoppable = false,
  inline = false,
  compact = false,
}) => {
  const [priceChangeCount, setPriceChangeCount] = useState(0);
  const prevPriceRef = useRef(displayPrice);
  const hasSettledRef = useRef(false);

  useEffect(() => {
    if (!hasPrice || !isPriceSettled) {
      hasSettledRef.current = false;
      prevPriceRef.current = displayPrice;
      return;
    }

    if (!hasSettledRef.current) {
      // First settlement — record silently, no animation.
      hasSettledRef.current = true;
      prevPriceRef.current = displayPrice;
      return;
    }

    if (displayPrice !== prevPriceRef.current) {
      prevPriceRef.current = displayPrice;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- incrementing the animation key is the effect's state machine transition.
      setPriceChangeCount((c) => c + 1);
    }
  }, [displayPrice, hasPrice, isPriceSettled]);

  // Key is stable on initial load (no enter animation), then changes on each
  // price update so AnimatePresence exits the old value and enters the new one.
  const priceKey =
    priceChangeCount === 0 ? "initial" : `${displayPrice}-${priceChangeCount}`;

  return (
    <div
      className={classNames(
        "shrink-0",
        inline
          ? "flex w-fit items-center gap-1.5 text-center lg:text-left"
          : "flex min-h-[52px] items-center justify-between gap-4 border-b border-zinc-800 bg-primaryBg px-5 py-3",
        inline && !compact && hasPrice ? "border-r border-zinc-700 pr-4" : "",
      )}
    >
      {!inline && (
        <span className="text-lg font-medium text-secondary">
          Estimated cost
        </span>
      )}
      <AnimatePresence>
        {hasPrice && (
          <motion.div
            className={classNames(
              "flex items-center gap-1",
              inline
                ? "justify-center text-center lg:justify-start lg:text-left"
                : "justify-end text-right",
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {priceLabel ? (
              <span
                className={classNames(
                  "font-bold text-highlight",
                  compact ? "text-base" : "text-lg",
                )}
              >
                {priceLabel}
              </span>
            ) : (
              <TooltipPrimitives.Provider>
                <TooltipPrimitives.Root delayDuration={0}>
                  <TooltipPrimitives.Trigger asChild>
                    <button className="flex items-center gap-1 text-primary">
                      {isFreeTier && isStorageFree ? (
                        <span
                          className={classNames(
                            "font-bold",
                            compact ? "text-base" : "text-lg",
                          )}
                        >
                          <FreeTierPrice price={displayPrice} />
                        </span>
                      ) : isFreeTier ? (
                        <span
                          className={classNames(
                            "font-bold",
                            compact ? "text-base" : "text-lg",
                          )}
                        >
                          <span className="line-through opacity-50">
                            ${displayPrice}/hr
                          </span>{" "}
                          ${storageCost.toFixed(2)}/hr
                        </span>
                      ) : isStorageFree && storageCost > 0 ? (
                        <span
                          className={classNames(
                            "font-bold",
                            compact ? "text-base" : "text-lg",
                          )}
                        >
                          <span className="line-through opacity-50">
                            ${displayPrice}/hr
                          </span>{" "}
                          ${computePrice.toFixed(2)}/hr
                        </span>
                      ) : (
                        <AnimatePresence mode="wait">
                          <motion.span
                            key={priceKey}
                            initial={{
                              opacity: priceChangeCount === 0 ? 1 : 0,
                            }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            className={classNames(
                              "font-bold text-primary",
                              compact ? "text-base" : "text-lg",
                            )}
                          >
                            ${displayPrice}/hr
                          </motion.span>
                        </AnimatePresence>
                      )}
                      <InformationCircleIcon className="h-4 w-4 text-secondary flex-shrink-0" />
                    </button>
                  </TooltipPrimitives.Trigger>
                  <TooltipPrimitives.Portal>
                    <TooltipPrimitives.Content
                      side="bottom"
                      align="end"
                      sideOffset={4}
                      className="z-50 w-max rounded-md bg-zinc-800 px-3 py-2.5 text-sm text-white shadow-md"
                    >
                      <PricingRateRows
                        computePrice={computePrice}
                        storageCost={storageCost}
                        pricePerGbHr={pricePerGbHr}
                        isFreeTierCompute={isFreeTier}
                        isFreeTierStorage={isStorageFree}
                      />
                      {stoppable &&
                        (!pricePerGbHr || isStorageFree ? (
                          <p className="text-sm text-zinc-400 mt-3">
                            You will not be charged while this instance is
                            stopped.
                          </p>
                        ) : (
                          <p className="text-sm font-normal text-white mt-3">
                            While stopped:{" "}
                            <span className="font-bold">
                              ${storageCost.toFixed(2)}/hr
                            </span>{" "}
                            for storage
                          </p>
                        ))}
                      {(disclaimer || isEntitled) && (
                        <div className="mt-2.5 pt-2.5 border-t border-zinc-700 w-full flex flex-col gap-2">
                          {disclaimer && (
                            <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">
                              {disclaimer}
                            </p>
                          )}
                          {isEntitled && (
                            <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">
                              {FREE_TIER_NOTE}
                            </p>
                          )}
                        </div>
                      )}
                      <TooltipPrimitives.Arrow className="fill-zinc-800" />
                    </TooltipPrimitives.Content>
                  </TooltipPrimitives.Portal>
                </TooltipPrimitives.Root>
              </TooltipPrimitives.Provider>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
